import { Alert, Flex, Select, Skeleton, Typography, theme } from 'antd'
import { WalletOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { formatMoneyValue } from '@/shared/lib/money'
import type { CashierState } from './useCashier'

interface CashierBalanceBarProps {
  cashier: CashierState
  onSelectAgent: (agentId: string) => void
}

/**
 * Kassirning DEPOZITI — ekranning eng tepasida va doim ko'rinadi.
 *
 * ⚠️ YOPISHQOQ (`position: sticky`) va bu qaror talabdan kelib chiqadi:
 * depozit tugasa kassirning ishi to'xtaydi. Ro'yxatni pastga aylantirgan
 * kassir uchun ham son ekranda qolishi kerak — aks holda u qolgan pulini
 * faqat rad javobidan, abonent qarshisida bilib olardi.
 *
 * ⚠️ IKKI SON ko'rsatiladi va ular BOSHQA narsa: `balance` — depozitda
 * haqiqatan turgani, `available` — qarz limiti qo'shilgani, ya'ni hozir
 * sarflash mumkin bo'lgani (`AgentBalance`). Ular teng bo'lsa ikkinchisi
 * chizilmaydi — bir xil sonni ikki marta ko'rsatish shovqin.
 */
export function CashierBalanceBar({ cashier, onSelectAgent }: CashierBalanceBarProps) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const spendable = cashier.balance?.available ?? null
  const empty = spendable !== null && spendable.amount <= 0
  const showAvailable =
    cashier.balance !== null && cashier.balance.available.amount !== cashier.balance.balance.amount

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        background: empty ? token.colorErrorBg : token.colorFillQuaternary,
        border: `1px solid ${empty ? token.colorErrorBorder : token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: '10px 14px',
        marginBottom: 12,
      }}
    >
      <Flex align="center" justify="space-between" gap={12} wrap>
        <Flex align="center" gap={10} style={{ minWidth: 0 }}>
          <WalletOutlined style={{ fontSize: 20, color: token.colorTextTertiary }} />
          <div style={{ minWidth: 0 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {t('cashdesk.myDeposit')}
            </Typography.Text>
            {cashier.loading ? (
              <Skeleton.Input active size="small" style={{ width: 140 }} />
            ) : (
              <Typography.Text
                strong
                type={empty ? 'danger' : undefined}
                style={{ fontSize: 20, lineHeight: 1.2 }}
              >
                {formatMoneyValue(cashier.balance?.balance)}
              </Typography.Text>
            )}
          </div>
        </Flex>

        {cashier.agents.length > 1 ? (
          <Select
            value={cashier.agent?.id ?? undefined}
            onChange={onSelectAgent}
            placeholder={t('cashdesk.chooseAgent')}
            style={{ minWidth: 180, flex: '1 1 180px' }}
            options={cashier.agents.map((a) => ({
              value: a.id,
              label: `${a.code} — ${a.displayName}`,
            }))}
          />
        ) : null}
      </Flex>

      {showAvailable ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('cashdesk.availableWithDebt', {
            amount: formatMoneyValue(cashier.balance?.available),
          })}
        </Typography.Text>
      ) : null}

      {empty ? (
        <Typography.Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          {t('cashdesk.depositEmpty')}
        </Typography.Text>
      ) : null}

      {cashier.noActiveAgent ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={t('cashdesk.noAgent')}
        />
      ) : null}

      {cashier.error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 8 }}
          message={translateError(cashier.error, t)}
        />
      ) : null}
    </div>
  )
}
